import { useState, useRef, useCallback } from 'react';
import type { PointerEventHandler } from 'react';

import { IConverterTabsScrollControls } from '@src/types';

export const useTabsScrollControls = (
  sharedScrollLeftRef?: React.MutableRefObject<number>
): IConverterTabsScrollControls => {
  // The DOM node for the tabs list (set via callback ref)
  const tabsElementRef = useRef<HTMLDivElement | null>(null);
  // Tracks pointer drag data (are we dragging, pointer start point, starting scroll left, pointer id)
  const dragStateRef = useRef({ isDragging: false, startX: 0, scrollLeft: 0, pointerId: 0 });
  const fallbackScrollLeftRef = useRef(0);
  // When provided, two dropdowns share this scroll position reference
  const effectiveScrollLeftRef = sharedScrollLeftRef ?? fallbackScrollLeftRef;
  const [isDraggingTabs, setIsDraggingTabs] = useState(false);

  const handleTabsWheel = useCallback(
    (event: WheelEvent) => {
      const element = tabsElementRef.current;
      if (!element) return;
      const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (delta === 0) return;

      element.scrollBy({
        left: delta,
        behavior: 'smooth',
      });
      // scrollBy is async, so capture the final value on the next frame
      requestAnimationFrame(() => {
        if (tabsElementRef.current) {
          effectiveScrollLeftRef.current = tabsElementRef.current.scrollLeft;
        }
      });

      event.preventDefault();
      event.stopPropagation();
    },
    [effectiveScrollLeftRef]
  );

  const setTabsRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (tabsElementRef.current) {
        tabsElementRef.current.removeEventListener('wheel', handleTabsWheel);
      }
      tabsElementRef.current = node;
      if (node) {
        node.scrollLeft = effectiveScrollLeftRef.current;
        node.addEventListener('wheel', handleTabsWheel, { passive: false });
      }
    },
    [effectiveScrollLeftRef, handleTabsWheel]
  );

  const handleTabsPointerEnd = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      const element = tabsElementRef.current;
      if (!element) return;

      if (dragStateRef.current.isDragging) {
        // Finish the drag and persist the latest scroll position
        dragStateRef.current.isDragging = false;
        setIsDraggingTabs(false);
        element.releasePointerCapture?.(dragStateRef.current.pointerId);
        effectiveScrollLeftRef.current = element.scrollLeft;
        event?.preventDefault();
        event?.stopPropagation();
      }

      dragStateRef.current.pointerId = 0;
    },
    [effectiveScrollLeftRef]
  );

  const handleTabsPointerDown = useCallback<PointerEventHandler<HTMLDivElement>>((event) => {
    const element = tabsElementRef.current;
    if (!element) return;

    dragStateRef.current = {
      isDragging: false,
      startX: event.clientX,
      scrollLeft: element.scrollLeft,
      pointerId: event.pointerId,
    };
  }, []);

  const handleTabsPointerMove = useCallback<PointerEventHandler<HTMLDivElement>>(
    (event) => {
      const element = tabsElementRef.current;
      if (!element) return;
      if (dragStateRef.current.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - dragStateRef.current.startX;

      if (!dragStateRef.current.isDragging) {
        if (Math.abs(deltaX) < 3) {
          return;
        }
        dragStateRef.current.isDragging = true;
        setIsDraggingTabs(true);
        element.setPointerCapture?.(dragStateRef.current.pointerId);
      }

      element.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
      effectiveScrollLeftRef.current = element.scrollLeft;
      event.preventDefault();
      event.stopPropagation();
    },
    [effectiveScrollLeftRef]
  );

  return {
    tabsRef: setTabsRef,
    isDraggingTabs,
    handleTabsPointerDown,
    handleTabsPointerMove,
    handleTabsPointerEnd,
  };
};
